import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { logout } from "../actions/authActions";
import { SYSTEM_HEARTBEAT } from "../actions/constants";
import {
  AuthState,
  selectIsAccessTokenExpired,
  selectIsRefreshTokenExpired,
} from "../reducers/authReducer";

// AuthGate should only be rendered if there is an unexpired Access or Refresh JWT
// It will render a LoadingEl Component until the following conditions are met:
//   1. Access Token is unexpired

const AuthGate = ({
  children,
  LoadingEl,
}: {
  children: React.ReactNode;
  LoadingEl: ({
    className,
    children,
  }: {
    className?: string;
    children?: React.ReactNode;
  }) => React.ReactElement;
}) => {
  const dispatch = useDispatch() as Function;
  const isAccessTokenExpired = useSelector(selectIsAccessTokenExpired);
  const isRefreshTokenExpired = useSelector(selectIsRefreshTokenExpired);
  const accessTokenUserId = useSelector(
    ({ auth }: { auth: AuthState }) => auth.access?.user_id
  );
  const loadedUserId = useSelector(
    ({ auth }: { auth: AuthState }) => auth.user?.id
  );

  if (!!loadedUserId && loadedUserId !== accessTokenUserId) {
    throw new Error("The loaded User does not match the Access Token");
  }

  useEffect(() => {
    if (isRefreshTokenExpired) {
      dispatch(logout());
    } else {
      // Start Heartbeat on mount (logged in)
      dispatch({
        type: SYSTEM_HEARTBEAT,
        payload: {
          index: 0,
        },
      });
    }
  }, [isRefreshTokenExpired]);

  if (isRefreshTokenExpired) return null;

  if (isAccessTokenExpired)
    return (
      <LoadingEl className="AuthGate">Refreshing Access Token...</LoadingEl>
    );

  return <>{children}</>;
};

export default AuthGate;
